import React from 'react';
import { useNavigate } from 'react-router-dom';

import { VerticalDots } from 'assets/img';
import ButtonViewV3 from 'components/ButtonViewV3/ButtonViewV3';
import OverFlowMenuView from 'components/OverFlowMenuView';
import TextView from 'components/TextView';
import { ButtonType } from 'features/Yucollect/Content/Channels/CategoriesTemplate';
import BackButtonWithHeading from 'features/Yucollect/shared/BackButton/BackButtonWithHeading';
import styled from 'styled-components';
import AuthProvider from 'templates/AuthProvider';

import YbCoreBreadCrumb from '@yubi/yb-core-breadcrumb';
import { YbCoreButton } from '@yubi/yb-core-button-v3';
import { useStyles } from '@yubi/yb-style-provider';

export interface MenuItems {
  label: string;
  key: string;
  disabled?: boolean;
  onClick: () => void;
}
export interface IPageAction {
  name: React.ReactElement | string;
  onClick: () => void;
  variant: ButtonType;
  disabled?: boolean;
  permission?: string[];
  hasDropdown?: boolean;
  menuItems?: MenuItems[];
}

interface IPageHeaderProps {
  minTitle?: string;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: IPageAction[];
  breadcrumbs?: Breadcrumb[];
}

interface Breadcrumb {
  name: string;
  redirectPath?: string;
}

const HeaderContainer = styled.div`
  flex-direction: column;
  gap: 16px;
  padding: 24px 0;
  border-bottom: 1px solid #e9eef4;
`;

const TitleContainer = styled.div`
  font-size: 24px;
  line-height: 36px;
  font-weight: 700;
  color: #101828;
  display: flex;
  justify-content: space-between;
`;

const SubtitleContainer = styled.div`
  font-size: 14px;
  font-weight: 100;
  color: #667085;
  line-height: 20px;
`;

export default function PageHeader({
  minTitle,
  title,
  subtitle,
  actions,
  breadcrumbs,
}: IPageHeaderProps) {
  const { styleConnector } = useStyles();
  const nav = useNavigate();

  return (
    <HeaderContainer>
      {breadcrumbs ? (
        <YbCoreBreadCrumb
          styleConnector={styleConnector}
          type="link"
          onClick={(_, idx: any) => {
            nav((breadcrumbs && breadcrumbs[idx]?.redirectPath) || '');
          }}
        >
          {breadcrumbs.map((item, idx) => (
            <YbCoreBreadCrumb.Item
              keyId={idx.toString()}
              name={item.name}
              active={idx + 1 === breadcrumbs.length}
            />
          ))}
        </YbCoreBreadCrumb>
      ) : null}
      {minTitle && <SubtitleContainer>{minTitle}</SubtitleContainer>}
      <BackButtonWithHeading text={''} style={{ float: 'left' }} />
      <TitleContainer>
        <TextView
          color="#101828"
          size={24}
          weight="700"
          lineHeight={36}
          text={title}
        />
        <div className="flex gap-4 justify-center items-center">
          {actions?.map((action) => (
            <AuthProvider
              permission={action.permission || []}
              key={action.name.toString()}
            >
              {!action.hasDropdown && (
                <div className="w-fit">
                  <YbCoreButton
                    variant={action.variant || 'primary'}
                    onClick={action.onClick}
                    styleConnector={styleConnector}
                    size="s"
                    label={action.name}
                    disabled={action.disabled || false}
                  />
                </div>
              )}
              {action.hasDropdown && (
                <div className="w-fit">
                  <OverFlowMenuView
                    items={action.menuItems}
                    icon={
                      <div className="flex w-full h-full  justify-center items-center">
                        <div className="flex w-full h-full justify-center items-center">
                          <ButtonViewV3
                            variant="tertiary"
                            label="Actions"
                            trailingIcon={VerticalDots}
                            onClick={() => {}}
                          />
                        </div>
                      </div>
                    }
                  />
                </div>
              )}
            </AuthProvider>
          ))}
        </div>
      </TitleContainer>
      {subtitle && (
        <div className="mt-[-8px]">
          <TextView
            color="#667085"
            size={14}
            weight="100"
            lineHeight={20}
            text={subtitle}
          />
        </div>
      )}
    </HeaderContainer>
  );
}
